import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Zap, Timer, Users, Sparkles, TrendingUp, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TID } from "@/constants/tid";
import { api } from "@/lib/api";
import AcronymReveal from "@/components/AcronymReveal";
import { SellerSpotlight } from "@/components/SellerSpotlight";
import WaitlistForm from "@/components/WaitlistForm";
import LogisticsNotice from "@/components/LogisticsNotice";
import { CategoryMarquee, TraderExperiences, HeroMontage } from "@/components/LandingShowcase";
import { WelcomeBackBanner, StickyFounderBar, useVisitCounter } from "@/components/ConversionBar";
import { CampaignWelcome } from "@/components/CampaignWelcome";
import { useI18n } from "@/i18n/I18nContext";

function CountUp({ value, prefix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    if (target === 0) { setN(0); return; }
    const start = performance.now();
    const dur = 1200;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{prefix}{n.toLocaleString()}</>;
}

function useLiveMetrics() {
  const [m, setM] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/analytics/public");
        if (!cancelled) setM(data);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return m;
}

function useFoundersPromo() {
  const [p, setP] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/billing/founders-promo");
        if (!cancelled) setP(data);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return p;
}

function Marquee({ metrics }) {
  const items = [
    `LIVE PRODUCTS: ${(metrics?.live_products ?? 0).toLocaleString()}`,
    `GMV 24H: $${Math.round(metrics?.gmv_24h ?? 0).toLocaleString()}`,
    "EVERY LISTING · ALWAYS LIVE · AI-MATCHED BUYERS",
    "DIRECT SELLER PAYOUTS",
  ];
  const row = items.map((t, i) => (
    <span key={i} className="mx-6 inline-flex items-center gap-3">
      <span className="h-1.5 w-1.5 rounded-full bg-white/80 inline-block" /> {t}
    </span>
  ));
  return (
    <div className="bg-[#FF3B30] overflow-hidden py-2" data-testid="landing-marquee">
      <div className="vsk-marquee-track font-mono font-extrabold text-xs text-white tracking-wide">
        <span>{row}</span>
        <span aria-hidden="true">{row}</span>
      </div>
    </div>
  );
}

export default function Landing() {
  const metrics = useLiveMetrics();
  const promo = useFoundersPromo();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const cmp = params.get("cmp");
  useVisitCounter();
  return (
    <div>
      <Marquee metrics={metrics} />
      <WelcomeBackBanner />
      {cmp && <CampaignWelcome code={cmp} />}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <HeroMontage />
        <div className="absolute inset-0 pointer-events-none">
          <div className="vsk-glow absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-[#007AFF]/30 blur-[120px]" />
          <div className="vsk-glow-slow absolute top-40 -left-40 h-[400px] w-[400px] rounded-full bg-[#FF3B30]/25 blur-[120px]" />
          <div className="vsk-glow absolute bottom-0 left-1/3 h-[300px] w-[500px] rounded-full bg-[#FF9F0A]/15 blur-[130px]" style={{ animationDelay: "4s" }} />
          <div className="vsk-light-sweep" />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32">
          <div className="max-w-3xl">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#34C759]" />
                  </span>
                  <span className="text-xs font-medium text-neutral-300" data-testid="hero-live-count">
                    Live: <span className="font-mono tabular-nums text-white">{(metrics?.live_products ?? 0).toLocaleString()}</span> products live right now
                  </span>
                </div>
                {promo && promo.remaining > 0 && (
                  <Link to="/register" data-testid="hero-founders-promo" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF3B30]/50 bg-[#FF3B30]/10 hover:bg-[#FF3B30]/20 transition-colors">
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#FF3B30]">Founders offer</span>
                    <span className="text-xs text-white">First month free — {promo.remaining} spot{promo.remaining === 1 ? "" : "s"} left</span>
                  </Link>
                )}
              </div>

              <h1 className="font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                {t("hero_line1", "A million products.")}
                <br />
                <span className="text-[#FF3B30]">{t("hero_line2_red", "Always live.")}</span> {t("hero_line2_rest", "Always selling.")}
              </h1>
              <p className="mt-6 max-w-xl text-neutral-400 text-base sm:text-lg leading-relaxed">
                {t("hero_sub", "Your catalogue, in front of the right buyers — every hour of every day. VISMARKS turns high-volume inventory into high-velocity sales. Upload thousands of products in minutes, let AI write, match and promote, and sell while you sleep.")}
              </p>

              <AcronymReveal />

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/register" data-testid={TID.heroCta}>
                  <Button size="lg" className="bg-[#FF3B30] hover:bg-[#e0352b] text-white rounded-full px-6 h-12 text-sm font-semibold shadow-[0_0_28px_rgba(255,59,48,0.35)] hover:shadow-[0_0_40px_rgba(255,59,48,0.55)]" style={{ transitionProperty: "background-color, box-shadow", transitionDuration: "200ms" }}>
                    {t("cta_start", "Start selling")} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/discover" data-testid={TID.heroSecondary}>
                  <Button size="lg" variant="outline" className="rounded-full px-6 h-12 text-sm font-semibold bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">
                    {t("cta_browse", "Browse live drops")}
                  </Button>
                </Link>
              </div>

              <div className="mt-14 grid grid-cols-2 gap-6 max-w-sm" data-testid="hero-metrics">
                <Stat label={t("stat_live", "Live products")} value={<CountUp value={metrics?.live_products ?? 0} />} />
                <Stat label={t("stat_gmv", "GMV 24h")} value={<CountUp value={Math.round(metrics?.gmv_24h ?? 0)} prefix="$" />} />
              </div>

              <div className="mt-8 max-w-md">
                <WaitlistForm source="landing-hero" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <CategoryMarquee />

      {/* FEATURES */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-widest text-[#007AFF] mb-3">The visibility engine</div>
            <h2 className="font-black tracking-tight text-4xl leading-tight">
              Built for one million listings a day.
            </h2>
            <p className="text-neutral-400 mt-4">
              Publish your catalogue once, let advanced AI match every listing to ready buyers,
              and watch products convert to sales around the clock.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/5 border border-white/10">
            <Feature
              icon={Timer}
              title="Always-on visibility"
              body="Your products stay live until you remove them — with fresh drops surfacing first, urgency stays high."
            />
            <Feature
              icon={Sparkles}
              title="AI buyer matching"
              body="Claude Sonnet 4.5 ranks live drops against each buyer's stated interests in real time."
            />
            <Feature
              icon={TrendingUp}
              title="Real-time analytics"
              body="Views, inquiries and daily conversion tracked per SKU for sellers and admins."
            />
            <Feature
              icon={Package}
              title="Bulk-ready catalog"
              body="Structured product schema designed to scale to millions of active SKUs per client."
            />
            <Feature
              icon={Users}
              title="Role-based access"
              body="Distinct dashboards for sellers, buyers and platform admins. Zero role confusion."
            />
            <Feature
              icon={Zap}
              title="Instant publishing"
              body="Publish and start the visibility clock in under a second. No approval queue."
            />
          </div>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="font-black tracking-tight text-4xl leading-tight">
              Publish. Get matched. Get paid.
            </h2>
            <p className="text-neutral-400 mt-4">
              VISMARKS pairs your entire catalogue with AI-matched buyers — always live, always converting.
              Built for sellers who move volume. List your first product in under a minute.
            </p>
          </div>
          <Link to="/register">
            <Button size="lg" className="bg-[#007AFF] hover:bg-[#0066d3] text-white rounded-full px-6 h-12">
              Create account <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <TraderExperiences />

      {/* SELLER VOICES */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#FF3B30] mb-3">Seller voices</div>
              <h2 className="font-black tracking-tight text-4xl leading-tight">Built for sellers in a hurry.</h2>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 border border-white/10 rounded-full px-3 py-1.5" data-testid="testimonials-disclaimer">
              Illustrative examples — real seller stories coming soon
            </span>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-strip">
            <Quote
              text="Listed my whole catalog Monday morning. By evening, matched buyers were already messaging."
              name="Ada"
              place="Lagos · Fashion"
            />
            <Quote
              text="Fresh drops move fast here. No more listings rotting on page 40 of a marketplace."
              name="Tunde"
              place="Abuja · Electronics"
            />
            <Quote
              text="Payments go straight to my bank with the fee already handled. Nothing to reconcile at month end."
              name="Maria"
              place="Nairobi · Home goods"
            />
          </div>
        </div>
      </section>

      <SellerSpotlight />

      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid md:grid-cols-[1fr_2fr] gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#007AFF] mb-3">How it works</div>
              <h2 className="font-black tracking-tight text-3xl leading-tight">
                We drive sales. Sellers drive trucks.
              </h2>
            </div>
            <div>
              <LogisticsNotice />
              <p className="mt-4 text-sm text-neutral-500 leading-relaxed">
                VISMARKS is the Visibility Intelligence Systems for high-volume sellers — publish your
                catalogue, get matched to buyers by advanced AI, and give every listing the chance to
                convert to sales, day after day, without expiry. Every seller is responsible for packing,
                shipping, delivery timelines, and returns on their own products. Buyers arrange delivery
                directly with the seller after purchase.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 text-xs text-neutral-500 flex flex-wrap items-center justify-between gap-4">
          <div>&copy; {new Date().getFullYear()} Vismarks. All rights reserved.</div>
          <div className="font-mono">v1.0 &middot; built for scale</div>
        </div>
      </footer>
      <StickyFounderBar promo={promo} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-mono tabular-nums text-3xl font-black mt-1">{value}</div>
    </div>
  );
}

function Quote({ text, name, place }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-7">
      <div className="text-[#FF3B30] font-black text-3xl leading-none">&ldquo;</div>
      <p className="mt-2 text-neutral-300 leading-relaxed text-sm">{text}</p>
      <div className="mt-5 flex items-center gap-3">
        <span className="h-8 w-8 rounded-full bg-[#007AFF]/15 border border-[#007AFF]/30 inline-flex items-center justify-center text-xs font-bold text-[#007AFF]">
          {name[0]}
        </span>
        <div>
          <div className="text-sm font-semibold text-white">{name}</div>
          <div className="text-[11px] text-neutral-500">{place}</div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="bg-[#0A0A0A] p-8 hover:bg-[#121212]" style={{ transitionProperty: "background-color", transitionDuration: "200ms" }}>
      <div className="h-9 w-9 rounded-md bg-white/[0.03] border border-white/10 inline-flex items-center justify-center">
        <Icon className="h-4 w-4 text-[#007AFF]" />
      </div>
      <h3 className="mt-6 font-bold tracking-tight text-lg">{title}</h3>
      <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{body}</p>
    </div>
  );
}
